import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody, StatTile, StatusDot, EmptyState } from "@/components/ui-bits";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, PlayCircle, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [s, t, a] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/telemetry", { params: { minutes: 60, limit: 500 } }),
      api.get("/alerts", { params: { limit: 6 } }),
    ]);
    setSummary(s.data);
    setTelemetry(t.data);
    setAlerts(a.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const simulate = async () => {
    toast.loading("Simulating telemetry burst…", { id: "sim" });
    await api.post("/telemetry/simulate");
    toast.success("Simulated readings ingested", { id: "sim" });
    load();
  };

  // Reduce telemetry into a series per metric for the chart
  const series = {};
  telemetry.forEach((r) => {
    series[r.metric] = series[r.metric] || [];
    series[r.metric].push({ ts: r.ts, [r.metric]: r.value });
  });
  // pick top metric
  const topMetric = Object.keys(series)[0];
  const chartData = (series[topMetric] || []).slice(-60).map((p) => ({
    t: new Date(p.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    v: p[topMetric],
  }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="// CONTROL ROOM"
        title="Fleet overview"
        description="Real-time status across every sensor connected to your account."
        actions={
          <button
            onClick={simulate}
            data-testid="simulate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white text-sm font-semibold"
          >
            <PlayCircle className="w-4 h-4" /> Simulate telemetry
          </button>
        }
      />
      <PageBody>
        {loading ? (
          <div className="label-eyebrow text-neutral-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatTile testid="stat-total" label="Total devices" value={summary.devices.total} />
              <StatTile testid="stat-online" label="Online" value={summary.devices.online} intent="success" />
              <StatTile testid="stat-warning" label="Warning" value={summary.devices.warning} intent="warning" />
              <StatTile testid="stat-alert" label="Alert" value={summary.devices.alert} intent="danger" />
              <StatTile testid="stat-readings" label="Readings · 24h" value={summary.readings_24h} sub={`${summary.anomalies_24h} anomalies`} />
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
              {/* Telemetry chart */}
              <div className="lg:col-span-2 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-eyebrow text-neutral-500">// LIVE TELEMETRY</div>
                    <div className="font-display text-xl font-semibold mt-1">
                      {topMetric ? `${topMetric} · last hour` : "No telemetry"}
                    </div>
                  </div>
                  <Link to="/telemetry" className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="open-telemetry-link">
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid stroke="#E5E5E5" strokeDasharray="2 2" vertical={false} />
                        <XAxis dataKey="t" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A3A3A3" />
                        <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A3A3A3" />
                        <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                        <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    title="No readings yet"
                    body="Hit 'Simulate telemetry' or send a real reading from a device."
                  />
                )}
              </div>

              {/* Recent alerts */}
              <div className="bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="label-eyebrow text-neutral-500">// RECENT ALERTS</div>
                  <Link to="/alerts" className="text-xs font-semibold hover:text-[#002FA7]" data-testid="open-alerts-link">View all</Link>
                </div>
                {alerts.length === 0 ? (
                  <div className="text-sm text-neutral-500">No alerts in the queue.</div>
                ) : (
                  <ul className="divide-y divide-neutral-200">
                    {alerts.map((a) => (
                      <li key={a.alert_id} className="py-3" data-testid={`alert-row-${a.alert_id}`}>
                        <div className="flex items-center gap-2">
                          <StatusDot status="alert" />
                          <div className="font-mono text-xs text-neutral-500">{new Date(a.ts).toLocaleString()}</div>
                        </div>
                        <div className="mt-1 text-sm">{a.message}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-8 border border-neutral-200 bg-white p-6 flex items-start gap-4">
              <Sparkles className="w-5 h-5 text-[#002FA7] mt-1" />
              <div className="flex-1">
                <div className="label-eyebrow text-neutral-500">// AI</div>
                <div className="font-display text-xl font-semibold mt-1">Need an executive summary?</div>
                <p className="text-sm text-neutral-600 mt-1">
                  Claude Sonnet 4.5 will write a daily briefing with predicted risks and recommended actions.
                </p>
              </div>
              <Link
                to="/insights"
                data-testid="open-insights-link"
                className="px-4 py-2 bg-[#002FA7] text-white font-semibold text-sm hover:bg-[#001E70]"
              >
                Open AI Insights
              </Link>
            </div>
          </>
        )}
      </PageBody>
    </div>
  );
}
